'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { MfaFetchMessagesFn } from './mfa.types';

type UseLiveOtpCodeParams = {
  credentialId: string;
  mfaConfigId: string;
  fetchMfaMessages?: MfaFetchMessagesFn;
  initialCode?: string;
  enabled?: boolean;
};

export const useLiveOtpCode = ({
  credentialId,
  mfaConfigId,
  fetchMfaMessages,
  initialCode = '',
  enabled = true,
}: UseLiveOtpCodeParams) => {
  const [otpCode, setOtpCode] = useState(initialCode);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const requestIdRef = useRef(0);

  const fetchOtpCode = useCallback(async () => {
    if (!fetchMfaMessages || !enabled) {
      return;
    }

    const requestId = ++requestIdRef.current;

    setIsOtpLoading(true);

    try {
      const result = await fetchMfaMessages({
        credentialId,
        mfaConfigId,
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (result.success) {
        const latestCode =
          result.otpCode?.trim() ?? result.messages?.[0]?.content?.trim() ?? '';

        if (latestCode) {
          setOtpCode(latestCode);
        }
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error('Failed to fetch OTP code:', error);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsOtpLoading(false);
      }
    }
  }, [credentialId, enabled, fetchMfaMessages, mfaConfigId]);

  useEffect(() => {
    setOtpCode(initialCode);

    if (!enabled) {
      return;
    }

    void fetchOtpCode();

    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, fetchOtpCode, initialCode]);

  return {
    otpCode,
    isOtpLoading,
    fetchOtpCode,
  };
};
